import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  dataTypes,
  Table,
  withAnalytics,
  columnTypes
} from 'cat-ecommerce-alloy';
import { normalizeUrl } from '@app/utils';
import { CAT_PART, NON_CAT_PART } from '@app/constants/analyticsConstants';
import { availability as availabilityPropTypes } from '@app/components/common/Availability/Availability.proptypes';
import { sourcingDetailsPropTypes } from '@app/components/common/Availability/AvailabilityBySourceContent/AvailabilityBySourceContent.proptypes';
import GstTableHeaderDisclaimer from '@app/components/common/product/GstTableHeaderDisclaimer';
import Availability from '@app/components/common/Availability';
import ProductLink from '@app/components/common/product/ProductLink';
import ProductReplacementPartsPurchaseInfo from './ProductReplacementPartsPurchaseInfo';
import styles from './ProductReplacedPartsTable.module.scss';
import { useFitmentCheck, useAnalytics } from '@app/hooks';
import { getPartName } from '@app/utils/commonUtils';

const GA_LIST_NAME = 'PCC PDP Replacement Parts';

const ProductReplacedPartsTable = ({ replacementParts }) => {
  const [t] = useTranslation();
  const { buildProductImpressionEvent } = useAnalytics();
  const { getFitmentCheck } = useFitmentCheck();

  const currency = useSelector(s => s.dealerCustomer?.currency);
  const isGstAvailable = useSelector(s => s.dealerCustomer?.isGstAvailable);
  const gstIncludesTax = useSelector(s => s.dealerCustomer?.gstIncludesTax);
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const esiteURL = useSelector(s => s.common.esiteURL);

  const getReplacementPartURL = ({ pdpURL, seoUrlKeyword }) => {
    let updatedUrl = pdpURL;
    if (seoUrlKeyword) {
      updatedUrl = `${esiteURL}/${seoUrlKeyword}`;
    }
    return normalizeUrl(updatedUrl);
  };

  const purchaseInfoLabel = (
    <>
      {`${t('PRICE')} (${currency})`}
      {isGstAvailable && (
        <p className="mt-1 mb-0">
          <GstTableHeaderDisclaimer
            className="fw-normal"
            includeGST={gstIncludesTax}
          />
        </p>
      )}
    </>
  );

  const tableSchema = [
    {
      className: 'd-none d-md-table-cell',
      id: 'name',
      label: t('PART_NAME'),
      dataType: dataTypes.ALPHANUMERIC,
      columnType: columnTypes.STRETCHED
    },
    {
      dataType: dataTypes.NUMBER,
      className: 'd-none d-md-table-cell',
      id: 'quantity',
      label: t('REQUIRED_QUANTITY'),
      columnType: columnTypes.HUGGED
    },
    {
      className: 'd-none d-md-table-cell',
      id: 'availabilityInfo',
      label: t('AVAILABILITY'),
      dataType: dataTypes.ALPHANUMERIC,
      columnType: columnTypes.STRETCHED
    },
    {
      className: 'd-none d-md-table-cell',
      id: 'purchaseInfo',
      label: purchaseInfoLabel,
      dataType: dataTypes.ALPHANUMERIC,
      columnType: columnTypes.HUGGED
    },
    {
      className: 'd-md-none',
      id: 'mobileRow'
    }
  ];

  const data = (replacementParts ?? []).map((part, index) => ({
    availability: part.availability,
    sourcingDetailsInformation: part.sourcingDetails,
    catEntryId: part.itemCatEntryId,
    isCatPart: part.isCatPart,
    name: getPartName(part.partNumber, part.name),
    partNumber: part.partNumber,
    position: index + 1,
    price: part.discountedPrice,
    quantity: part.quantity,
    category: part.categoryPath,
    reactKey: part.uniqueId,
    unformattedUnitPrice: part.unformattedUnitPrice,
    unitPrice: part.unitPrice,
    url: getReplacementPartURL(part),
    weight: part.weight
  }));

  const getProductAnalyticsObject = ({
    availability,
    isCatPart,
    name,
    partNumber,
    position,
    category,
    unformattedUnitPrice,
    weight
  }) => ({
    availability,
    brand: isCatPart ? CAT_PART : NON_CAT_PART,
    checkFitment: getFitmentCheck(partNumber),
    relatedModel: model ?? '',
    name: getPartName(partNumber, name),
    partNumber,
    position,
    price: unformattedUnitPrice,
    category,
    productWeight: weight
  });

  const analyticsImpressionEvents = buildProductImpressionEvent(
    GA_LIST_NAME,
    data.map(node => getProductAnalyticsObject(node))
  );

  const nameRenderer = ({
    availability,
    isCatPart,
    name,
    partNumber,
    position,
    price,
    url,
    category,
    weight
  }) => (
    <ProductLink
      className="text-md text-univers"
      link={isCatPart ? url : undefined}
      productClickDetails={{
        ...getProductAnalyticsObject({
          availability,
          isCatPart,
          name,
          partNumber,
          position,
          price,
          url,
          category,
          weight
        }),
        list: GA_LIST_NAME
      }}
    >
      {name}
    </ProductLink>
  );

  const purchaseInfoRenderer = ({
    availability,
    catEntryId,
    price,
    unitPrice,
    sourcingDetailsInformation
  }) => (
    <ProductReplacementPartsPurchaseInfo
      availability={availability}
      sourcingDetailsInformation={sourcingDetailsInformation}
      catEntryId={catEntryId}
      price={price}
      unitPrice={unitPrice}
    />
  );

  const availabilityRenderer = ({
    availability = [],
    catEntryId,
    sourcingDetailsInformation
  }) => (
    <Availability
      availabilityInformation={availability}
      catEntryId={catEntryId}
      hideLabel={true}
      sourcingDetailsInformation={sourcingDetailsInformation}
    />
  );
  const quantityRenderer = ({ quantity }) => (
    <p className="text-md">{quantity}</p>
  );

  // TODO: Replace this with future reusable ProductOverview component?
  const mobileRenderer = item => (
    <>
      {nameRenderer(item)}
      <ProductReplacementPartsPurchaseInfo
        availability={item.availability}
        catEntryId={item.itemCatEntryId}
        className="mt-2"
        currency={currency}
        price={item.price}
        unitPrice={item.unitPrice}
        sourcingDetailsInformation={item.sourcingDetailsInformation}
      >
        {isGstAvailable && (
          <GstTableHeaderDisclaimer
            className="fw-normal"
            includeGST={gstIncludesTax}
          />
        )}
        <p className="text-sm my-2">
          <span className="text-sm fw-bold">
            {`${t('REQUIRED_QUANTITY')}: `}
          </span>
          {item.quantity}
        </p>
      </ProductReplacementPartsPurchaseInfo>
    </>
  );

  const customRenderers = {
    mobileRow: mobileRenderer,
    name: nameRenderer,
    purchaseInfo: purchaseInfoRenderer,
    quantity: quantityRenderer,
    availabilityInfo: availabilityRenderer
  };

  return (
    <Table
      analyticsEvents={[
        withAnalytics.createLoadEvent(analyticsImpressionEvents)
      ]}
      className={cx(
        'mt-4 mb-3',
        styles['replacement-parts__table'],
        styles['vertical-align-th-top']
      )}
      customRenderers={customRenderers}
      data={data}
      schema={tableSchema}
    />
  );
};

ProductReplacedPartsTable.propTypes = {
  replacementParts: PropTypes.arrayOf(
    PropTypes.shape({
      availability: availabilityPropTypes,
      name: PropTypes.string,
      quantity: PropTypes.number,
      uniqueId: PropTypes.string,
      sourcingDetailsInformation: sourcingDetailsPropTypes
    }).isRequired
  )
};

// This is memoized to help assure that the productImpression event is only fired once
export default React.memo(ProductReplacedPartsTable);
